import React, { FormEvent, useEffect, useState } from 'react';
import { BookOpenText, Lock, Moon, PenLine, ShieldCheck, Sparkles, Sun, User } from 'lucide-react';
import { antiBrushHeaders, trackEvent } from '../utils/analytics';

type AuthMode = 'login' | 'register';
const USERNAME_MIN_LENGTH = 6;
const USERNAME_MAX_LENGTH = 20;
const PASSWORD_MAX_LENGTH = 32;
const LOGIN_PASSWORD_MAX_LENGTH = 100;
const USERNAME_PATTERN = /^[A-Za-z0-9_]{6,20}$/;
const PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]{8,32}$/;
const USERNAME_ALLOWED_PATTERN = /^[A-Za-z0-9_]*$/;
const PASSWORD_ALLOWED_PATTERN = /^[A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]*$/;

interface AuthPageProps {
  initialMode: AuthMode;
  onHome: () => void;
  onSuccess: () => void;
}

const apiBaseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:65181'}${process.env.REACT_APP_API_VERSION === undefined ? '/api/v1' : process.env.REACT_APP_API_VERSION}`;

const AuthPage: React.FC<AuthPageProps> = ({ initialMode, onHome, onSuccess }) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [theme, setTheme] = useState(() => localStorage.getItem('lingbi-theme') || 'light');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageState, setMessageState] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('lingbi-theme', theme);
  }, [theme]);

  const setUsernameInput = (value: string) => {
    if (mode === 'register') {
      const compact = value.replace(/\s+/g, '');
      const sanitized = compact.replace(/[^A-Za-z0-9_]/g, '').slice(0, USERNAME_MAX_LENGTH);
      setUsername(sanitized);
      if (value !== compact || (compact && !USERNAME_ALLOWED_PATTERN.test(compact))) {
        setMessageState('error');
        setMessage('用户名只能使用字母、数字和下划线，不能输入中文、空格或特殊符号。');
      } else if (messageState === 'error') {
        setMessage('');
        setMessageState('idle');
      }
      return;
    }
    setUsername(value.slice(0, 50));
  };

  const setPasswordInput = (value: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
    if (mode === 'register') {
      const compact = value.replace(/\s+/g, '');
      const sanitized = compact.replace(/[^A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/g, '').slice(0, PASSWORD_MAX_LENGTH);
      setter(sanitized);
      if (value !== compact || (compact && !PASSWORD_ALLOWED_PATTERN.test(compact))) {
        setMessageState('error');
        setMessage('密码不能输入中文、空格或未支持的特殊符号。');
      } else if (messageState === 'error') {
        setMessage('');
        setMessageState('idle');
      }
      return;
    }
    setter(value.slice(0, LOGIN_PASSWORD_MAX_LENGTH));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const nextUsername = username.trim();
    if (!nextUsername || !password) {
      setMessageState('error');
      setMessage('请输入用户名和密码。');
      return;
    }
    if (mode === 'register' && !USERNAME_PATTERN.test(nextUsername)) {
      setMessageState('error');
      setMessage('用户名需为 6-20 位，只能包含字母、数字和下划线。');
      return;
    }
    if (mode === 'register' && !PASSWORD_PATTERN.test(password)) {
      setMessageState('error');
      setMessage('密码需为 8-32 位，至少包含字母和数字，不能包含中文或空格。');
      return;
    }
    if (mode === 'register' && password !== confirmPassword) {
      setMessageState('error');
      setMessage('两次输入的密码不一致。');
      return;
    }

    try {
      setMessageState('loading');
      setMessage(mode === 'login' ? '正在登录...' : '正在注册...');

      if (mode === 'register') {
        const response = await fetch(`${apiBaseUrl}/users_app/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...antiBrushHeaders() },
          body: JSON.stringify({ username: nextUsername, password, confirm_password: confirmPassword }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || '注册失败');
      }

      const loginResponse = await fetch(`${apiBaseUrl}/users_app/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...antiBrushHeaders() },
        body: JSON.stringify({ username: nextUsername, password }),
      });
      const loginData = await loginResponse.json();
      if (!loginResponse.ok) throw new Error(loginData.detail || '登录失败');

      localStorage.setItem('lingbi-token', loginData.token);
      localStorage.setItem('lingbi-user', JSON.stringify(loginData.user));
      trackEvent({
        event: mode === 'login' ? 'user_login' : 'user_register',
        page: window.location.hash || '#login',
        module: 'auth',
        action: mode,
        metadata: { username: nextUsername },
      });
      setMessageState('success');
      setMessage('登录成功，正在返回首页。');
      window.setTimeout(onSuccess, 360);
    } catch (error) {
      setMessageState('error');
      setMessage(error instanceof Error ? error.message : '请求失败，请稍后重试。');
    }
  };

  return (
    <main className={`auth-screen lumi-auth-screen lumi-auth-${mode}`}>
      <div
        className="lumi-auth-visual"
        aria-hidden="true"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/auth/lingbi-auth-studio.png)` }}
      >
        <span className="lumi-auth-light lumi-auth-light-one" />
        <span className="lumi-auth-light lumi-auth-light-two" />
        <span className="lumi-auth-grain" />
      </div>

      <header className="lumi-auth-header">
        <button className="lumi-auth-brand" type="button" onClick={onHome} aria-label="返回灵笔首页">
          <span><Sparkles size={18} /></span>
          <strong>灵笔</strong>
          <small>SPIRIT BRUSH</small>
        </button>
        <div className="lumi-auth-header-actions">
          <button className="back-home lumi-auth-back" type="button" onClick={onHome}>
            返回官网
          </button>
          <button className="lumi-theme-toggle lumi-auth-theme" type="button" onClick={() => setTheme((value) => (value === 'dark' ? 'light' : 'dark'))} aria-label="切换主题">
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      <section className="auth-intro lumi-auth-intro">
        <span className="lumi-auth-kicker">WRITE WITH LIGHT</span>
        <h1>
          让每一次落笔，
          <span>都有迹可循。</span>
        </h1>
        <p>从一句灵感到完整长篇，创作过程、作品资产和灵感片段都会安全归档。再次回来，故事仍在原处等你。</p>
        <div className="lumi-auth-trust" aria-label="平台能力">
          <span><PenLine size={16} />七步创作工作流</span>
          <span><BookOpenText size={16} />作品云端归档</span>
          <span><ShieldCheck size={16} />账号资产保护</span>
        </div>
        <div className="lumi-auth-caption">
          <i />
          <div>
            <span>灵感不会消失，只会等待被写下。</span>
            <small>LINGBI CREATIVE STUDIO</small>
          </div>
        </div>
      </section>

      <section className="auth-panel lumi-auth-panel">
        <div className="lumi-auth-panel-head">
          <span className="lumi-auth-panel-mark"><Sparkles size={16} /></span>
          <div>
            <small>{mode === 'login' ? 'WELCOME BACK' : 'START WRITING'}</small>
            <h2>{mode === 'login' ? '欢迎回来' : '创建灵笔账号'}</h2>
          </div>
        </div>
        <div className="auth-tabs">
          <button className={mode === 'login' ? 'active' : ''} type="button" onClick={() => setMode('login')}>登录</button>
          <button className={mode === 'register' ? 'active' : ''} type="button" onClick={() => setMode('register')}>注册</button>
        </div>
        {mode === 'login' && <p className="lumi-auth-panel-copy">登录后继续管理你的小说工程和创作资产。</p>}
        <form className="auth-form" onSubmit={submit} noValidate>
          <label>
            <span>用户名</span>
            <div className="input-shell">
              <User size={17} />
              <input
                value={username}
                onChange={(event) => setUsernameInput(event.target.value)}
                minLength={USERNAME_MIN_LENGTH}
                maxLength={mode === 'register' ? USERNAME_MAX_LENGTH : 50}
                pattern={mode === 'register' ? '[A-Za-z0-9_]{6,20}' : undefined}
                autoComplete="username"
                required
                placeholder="例如 lingbi_writer"
              />
            </div>
          </label>
          <label>
            <span>密码</span>
            <div className="input-shell">
              <Lock size={17} />
              <input
                value={password}
                onChange={(event) => setPasswordInput(event.target.value, setPassword)}
                type="password"
                minLength={mode === 'register' ? 8 : 6}
                maxLength={mode === 'register' ? PASSWORD_MAX_LENGTH : LOGIN_PASSWORD_MAX_LENGTH}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                required
                placeholder={mode === 'register' ? '8-32 位，含字母和数字' : '请输入密码'}
              />
            </div>
          </label>
          {mode === 'register' && (
            <label>
              <span>确认密码</span>
              <div className="input-shell">
                <Lock size={17} />
                <input
                  value={confirmPassword}
                  onChange={(event) => setPasswordInput(event.target.value, setConfirmPassword)}
                  type="password"
                  minLength={8}
                  maxLength={PASSWORD_MAX_LENGTH}
                  autoComplete="new-password"
                  required
                  placeholder="再次输入密码"
                />
              </div>
            </label>
          )}
          <button className="lumi-btn lumi-btn-primary auth-submit" type="submit">
            {mode === 'login' ? '登录' : '注册账号'}
          </button>
          <div className={`auth-message ${messageState}`}>{message}</div>
        </form>
        <div className="lumi-auth-legal">
          登录或注册即代表你已了解并同意灵笔的账号使用规则与隐私保护说明。
        </div>
      </section>
    </main>
  );
};

export default AuthPage;
